import React, { useState, useEffect } from 'react';
import { ShieldCheck, Key, Save, CheckCircle, AlertCircle, Eye, EyeOff, RefreshCw, Server } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from '../hooks/use-toast';
import SelectorModeloIA, { olvidarModelos } from './SelectorModeloIA';
import { aiSettingsAPI } from '../api';

const PROVIDERS = [
  { id: 'anthropic', name: 'Anthropic (Claude)', placeholder: 'sk-ant-...', modelHint: 'claude-sonnet-4-5-20250929', url: 'https://console.anthropic.com/settings/keys' },
  { id: 'openai', name: 'OpenAI (GPT)', placeholder: 'sk-proj-...', modelHint: 'gpt-5.5', url: 'https://platform.openai.com/api-keys' },
  { id: 'gemini', name: 'Google (Gemini)', placeholder: 'AIzaSy...', modelHint: 'gemini-2.5-pro', url: 'https://aistudio.google.com/app/apikey' },
  { id: 'deepseek', name: 'DeepSeek', placeholder: 'sk-...', modelHint: 'deepseek-chat', url: 'https://platform.deepseek.com/api_keys' },
  { id: 'qwen', name: 'Qwen (Aliyun)', placeholder: 'sk-...', modelHint: 'qwen-plus', url: 'https://bailian.console.aliyun.com/?apiKey=1' },
];

// Un valor vacio por cada proveedor, para no repetir la lista a mano.
const vacio = () => PROVIDERS.reduce((acc, p) => ({ ...acc, [p.id]: '' }), {});

const AIFallbackSettings = () => {
  const [modo, setModo] = useState('auto');
  const [providers, setProviders] = useState({});
  const [keys, setKeys] = useState(vacio());
  const [models, setModels] = useState(vacio());
  const [show, setShow] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [testResult, setTestResult] = useState({});

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await aiSettingsAPI.getFallback();
      setModo(data.modo || 'auto');
      setProviders(data.providers || {});
      setModels(PROVIDERS.reduce(
        (acc, p) => ({ ...acc, [p.id]: data.providers?.[p.id]?.model || '' }), {}));
    } catch (e) {
      console.error('Error cargando fallback IA', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { modo };
      // Solo enviar claves que el usuario ha escrito (no pisar las guardadas).
      PROVIDERS.forEach((p) => {
        if (keys[p.id]) payload[`${p.id}Key`] = keys[p.id];
        payload[`${p.id}Model`] = models[p.id] || '';
      });
      await aiSettingsAPI.updateFallback(payload);
      olvidarModelos();
      toast({ title: 'Guardado', description: 'Configuración de independencia IA guardada.' });
      setKeys(vacio());
      await load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (prov) => {
    setTesting((t) => ({ ...t, [prov]: true }));
    setTestResult((r) => ({ ...r, [prov]: null }));
    try {
      // Si hay clave nueva sin guardar, guardamos primero para poder probarla.
      if (keys[prov]) await handleSaveSilent();
      const { data } = await aiSettingsAPI.testFallback({ provider: prov });
      setTestResult((r) => ({ ...r, [prov]: { success: true, message: `${data.model}: ${(data.response || '').substring(0, 160)}` } }));
      toast({ title: 'Conexión directa OK', description: `${prov} responde correctamente.` });
    } catch (e) {
      setTestResult((r) => ({ ...r, [prov]: { success: false, message: e.response?.data?.detail || 'Error' } }));
      toast({ title: 'Error en test', description: `${prov}: ${e.response?.data?.detail || 'Error'}`, variant: 'destructive' });
    } finally {
      setTesting((t) => ({ ...t, [prov]: false }));
    }
  };

  const handleSaveSilent = async () => {
    const payload = { modo };
    PROVIDERS.forEach((p) => {
      if (keys[p.id]) payload[`${p.id}Key`] = keys[p.id];
      payload[`${p.id}Model`] = models[p.id] || '';
    });
    await aiSettingsAPI.updateFallback(payload);
    olvidarModelos();
    setKeys(vacio());
    await load();
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8"><RefreshCw className="w-6 h-6 animate-spin text-blue-600" /></div>;
  }

  return (
    <Card data-testid="ai-fallback-card" className="border-2 border-amber-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-amber-600" />
          Independencia de proveedor IA (Fallback)
        </CardTitle>
        <CardDescription>
          Evita quedarte sin IA si el servicio de Emergent falla. En modo automático se usa Emergent
          y, si falla, se cae a tu clave propia. En modo directo se usa siempre tu clave (sin Emergent).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Selector de modo */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div
            data-testid="ai-modo-auto"
            onClick={() => setModo('auto')}
            className={`p-4 rounded-xl border-2 cursor-pointer transition-colors ${modo === 'auto' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300'}`}
          >
            <div className="flex items-center gap-2 mb-1 font-semibold"><Server className="w-4 h-4 text-amber-600" />Automático (recomendado)</div>
            <p className="text-sm text-gray-600">Emergent primero. Si falla, usa tu clave propia sin que te enteres.</p>
          </div>
          <div
            data-testid="ai-modo-directo"
            onClick={() => setModo('directo')}
            className={`p-4 rounded-xl border-2 cursor-pointer transition-colors ${modo === 'directo' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300'}`}
          >
            <div className="flex items-center gap-2 mb-1 font-semibold"><Key className="w-4 h-4 text-amber-600" />Directo (sin Emergent)</div>
            <p className="text-sm text-gray-600">Se salta Emergent por completo y llama a tu clave propia.</p>
          </div>
        </div>

        {modo === 'directo' && (
          <div className="p-3 rounded-lg bg-amber-100 text-amber-800 text-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            En modo directo debes tener configurada la clave del proveedor que uses, o las funciones de IA fallarán.
          </div>
        )}

        {/* Claves por proveedor */}
        {PROVIDERS.map((p) => {
          const configured = providers[p.id]?.configurada;
          return (
            <div key={p.id} className="p-4 rounded-xl border border-gray-200 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">{p.name}</Label>
                {configured
                  ? <Badge className="bg-green-100 text-green-700" data-testid={`ai-key-status-${p.id}`}>Configurada {providers[p.id]?.maskedKey}</Badge>
                  : <Badge variant="outline" data-testid={`ai-key-status-${p.id}`}>Sin configurar</Badge>}
              </div>
              <div className="relative">
                <Input
                  data-testid={`ai-key-input-${p.id}`}
                  type={show[p.id] ? 'text' : 'password'}
                  value={keys[p.id]}
                  onChange={(e) => setKeys((k) => ({ ...k, [p.id]: e.target.value }))}
                  placeholder={configured ? '•••• (dejar vacío para mantener)' : p.placeholder}
                  className="pr-10"
                />
                <button type="button" onClick={() => setShow((s) => ({ ...s, [p.id]: !s[p.id] }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {show[p.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <SelectorModeloIA
                proveedor={p.id}
                value={models[p.id]}
                onChange={(valor) => setModels((m) => ({ ...m, [p.id]: valor }))}
                testId={`ai-model-input-${p.id}`}
              />
              <div className="flex items-center justify-between">
                <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">Obtener API key</a>
                <Button
                  data-testid={`ai-test-btn-${p.id}`}
                  variant="outline" size="sm"
                  disabled={testing[p.id] || (!configured && !keys[p.id])}
                  onClick={() => handleTest(p.id)}
                >
                  {testing[p.id] ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Probando…</> : 'Probar directo'}
                </Button>
              </div>
              {testResult[p.id] && (
                <div className={`text-xs p-2 rounded flex items-start gap-1 ${testResult[p.id].success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {testResult[p.id].success ? <CheckCircle className="w-3.5 h-3.5 mt-0.5" /> : <AlertCircle className="w-3.5 h-3.5 mt-0.5" />}
                  <span>{testResult[p.id].message}</span>
                </div>
              )}
            </div>
          );
        })}

        <Button data-testid="ai-fallback-save-btn" onClick={handleSave} disabled={saving} className="w-full bg-amber-600 hover:bg-amber-700">
          {saving ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Guardando…</> : <><Save className="w-4 h-4 mr-2" />Guardar independencia IA</>}
        </Button>
      </CardContent>
    </Card>
  );
};

export default AIFallbackSettings;
